import {type BskyAgent} from '@atproto/api'
import {type QueryClient} from '@tanstack/react-query'

import {uploadBlob} from '#/lib/api/upload-blob'
import {type Classroom} from '#/state/classrooms'
import {
  cacheClassroomView,
  classroomProcedure,
  type ClassroomViewJson,
  invalidateClassroom,
} from '#/state/queries/classrooms'

const CLASSROOM_FACE_COLLECTION = 'app.taderok.classroom'

/**
 * Uploads a classroom's new cover and returns the blob's cid, for the create or
 * update call to send as `coverCid`.
 */
export async function uploadClassroomCover(
  agent: BskyAgent,
  cover: string,
): Promise<{cid: string; blob: unknown}> {
  const {data} = await uploadBlob(agent, cover)
  return {cid: data.blob.ref.toString(), blob: data.blob}
}

type ClassroomFace = {id: string; name: string; handle: string; createdAt: string}

/**
 * Writes the room's public face into the owner's repo.
 *
 * The server holds the room; this record exists so the PDS keeps the cover blob
 * referenced (an unreferenced blob is garbage-collected) and link previews have
 * something to read. Without a blob the face carries no cover.
 */
export async function putClassroomFace(
  agent: BskyAgent,
  face: ClassroomFace,
  blob?: unknown,
): Promise<void> {
  const repo = agent.session?.did
  if (!repo) throw new Error('Not signed in')
  await agent.com.atproto.repo.putRecord({
    repo,
    collection: CLASSROOM_FACE_COLLECTION,
    rkey: face.id,
    record: {
      $type: CLASSROOM_FACE_COLLECTION,
      name: face.name,
      handle: face.handle,
      ...(blob ? {cover: blob} : {}),
      createdAt: face.createdAt,
    },
  })
}

/**
 * Rewrites the public face after a rename, keeping the cover blob it already
 * references, so link previews do not keep the old name.
 */
export async function renameClassroomFace(
  agent: BskyAgent,
  face: ClassroomFace,
): Promise<void> {
  const repo = agent.session?.did
  if (!repo) throw new Error('Not signed in')
  let cover: unknown
  try {
    const {data} = await agent.com.atproto.repo.getRecord({
      repo,
      collection: CLASSROOM_FACE_COLLECTION,
      rkey: face.id,
    })
    cover = (data.value as {cover?: unknown}).cover
  } catch {
    // No face yet: write one without a cover
  }
  await putClassroomFace(agent, face, cover)
}

/**
 * Gives a room that was just created its cover: the bytes go up, the public
 * face is written so the blob stays referenced, and only then is the server
 * told the cid - so the room never points at a blob the PDS may collect.
 */
export async function attachClassroomCover(
  agent: BskyAgent,
  queryClient: QueryClient,
  classroom: Classroom,
  cover: string,
): Promise<void> {
  const {cid, blob} = await uploadClassroomCover(agent, cover)
  await putClassroomFace(
    agent,
    {
      id: classroom.id,
      name: classroom.name,
      handle: classroom.handle,
      createdAt: classroom.createdAt,
    },
    blob,
  )
  const res = await classroomProcedure<{classroom: ClassroomViewJson}>(
    agent,
    'app.taderok.classroom.updateClassroom',
    {id: classroom.id, coverCid: cid},
  )
  cacheClassroomView(queryClient, res.classroom)
  await invalidateClassroom(queryClient, classroom.id)
}
